import React, { useState, useEffect } from 'react';
import { Package, LayoutGrid } from 'lucide-react';
import api from '../../lib/api';
import ProductCard from '../ProductCard';

/**
 * VitrinBlock — CMS Block for displaying Marketing Vitrines
 * 
 * Consumes the resolve API to display the winning vitrine for a placement.
 * Follows the same async pattern as ProductListBlock.
 * 
 * Content schema:
 *   placement_key: string (required) — marketing placement to resolve
 *   title: string (optional) — override vitrine title
 *   subtitle: string (optional) — custom subtitle
 *   display_mode: "auto" | "manual" — auto = resolve API, manual = direct IDs
 *   manual_vitrine_id: string (optional) — specific vitrine ID for manual mode
 *   layout: { desktop, tablet, mobile } — grid columns config
 *   show_title: boolean — whether to display the vitrine title
 */
const VitrinBlock = ({ block }) => {
  const [vitrine, setVitrine] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    placement_key,
    title,
    subtitle,
    display_mode = 'auto',
    manual_vitrine_id,
    layout,
    show_title = true,
  } = block.content || {};

  useEffect(() => {
    if (!placement_key && display_mode === 'auto') {
      setLoading(false);
      return;
    }
    loadVitrine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement_key, display_mode, manual_vitrine_id]);

  const loadVitrine = async () => {
    try {
      setError(null);
      setLoading(true);

      let vitrineData;
      let productsData;

      if (display_mode === 'manual' && manual_vitrine_id) {
        // Manual mode — fetch specific vitrine + its products
        const [vRes, pRes] = await Promise.all([
          api.get(`/vitrines/${manual_vitrine_id}`),
          api.get(`/vitrines/${manual_vitrine_id}/products`),
        ]);
        vitrineData = vRes.data;
        productsData = pRes.data || [];
      } else {
        // Auto mode — resolve the placement
        const res = await api.get(`/vitrines/resolve/${placement_key}`);
        if (res.status === 204 || !res.data) {
          setVitrine(null);
          setProducts([]);
          setLoading(false);
          return;
        }
        vitrineData = res.data;
        productsData = res.data.products || [];
      }

      setVitrine(vitrineData);
      setProducts(productsData);
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 204) {
        setVitrine(null);
        setProducts([]);
      } else {
        console.error('[VitrinBlock] Error:', err);
        setError('Impossible de charger la vitrine');
      }
    } finally {
      setLoading(false);
    }
  };

  const getGridClass = () => {
    // Priorité: columns/rows de la vitrine, puis layout CMS, puis défaut 4 colonnes
    const cols = vitrine?.columns || (layout?.desktop?.replace('grid-', '')) || '4';
    return `grid-cols-2 sm:grid-cols-${Math.min(parseInt(cols), 3)} lg:grid-cols-${cols}`;
  };

  // Limiter les produits affichés selon columns × rows
  const maxDisplay = (vitrine?.columns || 4) * (vitrine?.rows || 1);
  const displayProducts = products.slice(0, maxDisplay);

  const displayTitle = title || (show_title && vitrine?.title);
  const displaySubtitle = subtitle || vitrine?.description;

  // Loading state
  if (loading) {
    return (
      <div
        className="vitrin-block"
        data-testid="vitrin-block-loading"
        style={{ width: '100%', boxSizing: 'border-box' }}
      >
        {displayTitle && <h2 className="text-2xl font-heading font-semibold mb-2">{displayTitle}</h2>}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse rounded-lg bg-gray-100 aspect-[3/4]" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className="vitrin-block"
        data-testid="vitrin-block-error"
        style={{ width: '100%', boxSizing: 'border-box' }}
      >
        <p className="text-center text-red-500 py-8 text-sm">{error}</p>
      </div>
    );
  }

  // Empty state — silent (no vitrine resolved = no render)
  if (!vitrine || displayProducts.length === 0) {
    return null;
  }

  return (
    <div
      className="vitrin-block"
      data-testid="vitrin-block"
      data-block-type="vitrin_block"
      data-placement-key={placement_key}
      style={{ width: '100%', boxSizing: 'border-box' }}
    >
      {(displayTitle || displaySubtitle) && (
        <div className="mb-6">
          {displayTitle && (
            <h2 className="text-2xl font-heading font-semibold text-gray-900">{displayTitle}</h2>
          )}
          {displaySubtitle && (
            <p className="text-gray-500 mt-1">{displaySubtitle}</p>
          )}
        </div>
      )}
      <div className={`grid ${getGridClass()} gap-6`}>
        {displayProducts.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            variant="default"
            showTag={true}
          />
        ))}
      </div>
    </div>
  );
};

export default VitrinBlock;
